import { useCallback, useEffect, useRef, useState } from "react";
import React from "react";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { useAgency } from "@/context/AgencyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Building2, Save, Printer, Upload, X, ShieldCheck, Mail, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { DatePicker } from "@/components/DatePicker";
import SensitiveOtpGate from "@/components/SensitiveOtpGate";
import MobileAppDownloadButton from "@/components/MobileAppDownloadButton";
import { ExpiryReminderDaysCard } from "@/components/ExpiryReminderDaysCard";

const empty = {
  name: "Assicura", legal_name: "", vat_number: "", fiscal_code: "", rui_number: "", rui_date: "",
  address: "", city: "", cap: "", province: "",
  phone: "", email: "", pec: "", website: "", logo_url: "", motto: "",
  fiscal_regime: "", fiscal_regime_note: "",
  audit_stamp_color: "",  // 🆕 Feb 2026 — colore header timbro audit firma
};

// 🆕 Elenco regimi fiscali comuni per intermediari assicurativi in Italia
const FISCAL_REGIMES = [
  { code: "ordinario", label: "Ordinario (con IVA)" },
  { code: "semplificato", label: "Semplificato (con IVA)" },
  { code: "forfettario", label: "Forfettario (L. 190/2014, senza IVA)" },
  { code: "minimi", label: "Regime dei minimi (D.L. 98/2011) — legacy" },
  { code: "esente_iva_art10", label: "Esente IVA ex Art. 10 D.P.R. 633/72 (intermediazione assicurativa)" },
  { code: "altro", label: "Altro (specifica in nota)" },
];

export default function AgencySettings() {
  return (
    <SensitiveOtpGate
      title="Verifica accesso Anagrafica Agenzia"
      description="Per accedere ai dati dell'agenzia (denominazione, P.IVA, RUI, indirizzo, logo, regime fiscale, calendar, voci accessorie) conferma la tua identità con un codice OTP inviato all'email dell'amministratore."
    >
      <AgencySettingsForm />
    </SensitiveOtpGate>
  );
}

function AgencySettingsForm() {
  const { refresh: refreshAgency } = useAgency();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  // OTP dialog
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpStage, setOtpStage] = useState("confirm"); // "confirm" | "otp"
  const [otpEmail, setOtpEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/agency-settings"); setForm({ ...empty, ...data }); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // 🆕 Voci accessorie predefinite — UNIFICATE con la lista master "Voci offerta" (/offer-line-items)
  //     La sezione qui è una vista dedicata (mostra anche disattivate) ma opera sulla stessa collection.
  const [accItems, setAccItems] = useState([]);
  const [accForm, setAccForm] = useState({ name: "", default_premium: "", default_description: "", is_active: true, sort_order: 0 });
  const [accEditingId, setAccEditingId] = useState(null);
  // Sorgente dati per il Select "Nome voce" (solo voci attive, evita di riproporre quella in modifica)
  const offerItems = accItems.filter((it) => it.is_active !== false);
  const loadAccItems = useCallback(async () => {
    try {
      // Preferisci /offer-line-items/all (admin/backoffice: include disattivate)
      const { data } = await api.get("/offer-line-items/all");
      setAccItems(data || []);
    } catch {
      // Fallback: se non admin/backoffice usa la versione base (solo attive)
      try { const { data } = await api.get("/offer-line-items"); setAccItems(data || []); }
      catch { /* silent */ }
    }
  }, []);
  useEffect(() => { loadAccItems(); }, [loadAccItems]);
  const submitAccItem = async () => {
    if (!accForm.name.trim()) { toast.error("Nome obbligatorio"); return; }
    // Mapping unificato: default_description → description (schema offer_line_items)
    const payload = {
      name: accForm.name.trim(),
      default_premium: accForm.default_premium === "" ? null : Number(accForm.default_premium),
      description: accForm.default_description || "",
      policy_type: "",
      is_active: !!accForm.is_active,
      sort_order: Number(accForm.sort_order) || 100,
    };
    try {
      if (accEditingId) {
        await api.put(`/offer-line-items/${accEditingId}`, payload);
        toast.success("Voce aggiornata");
      } else {
        await api.post("/offer-line-items", payload);
        toast.success("Voce creata");
      }
      setAccForm({ name: "", default_premium: "", default_description: "", is_active: true, sort_order: 0 });
      setAccEditingId(null);
      loadAccItems();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const editAccItem = (it) => {
    setAccEditingId(it.id);
    setAccForm({
      name: it.name || "",
      default_premium: it.default_premium == null ? "" : String(it.default_premium),
      // Mapping bidirezionale: description (offer_line_items) ↔ default_description (form legacy)
      default_description: it.description || it.default_description || "",
      is_active: it.is_active !== false,
      sort_order: it.sort_order || 0,
    });
  };
  const deleteAccItem = async (it) => {
    if (!window.confirm(`Eliminare la voce "${it.name}"?`)) return;
    try { await api.delete(`/offer-line-items/${it.id}`); toast.success("Voce eliminata"); loadAccItems(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // 🆕 Google Calendar (OAuth) — collegamento per-utente
  const [gcalStatus, setGcalStatus] = useState({ configured: false, connected: false, email: null });
  const loadGcalStatus = useCallback(async () => {
    try { const { data } = await api.get("/oauth/calendar/status"); setGcalStatus(data); }
    catch { /* silent */ }
  }, []);
  useEffect(() => { loadGcalStatus(); }, [loadGcalStatus]);
  useEffect(() => {
    // Se il redirect callback arriva con ?google_calendar=connected, refresh + toast
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("google_calendar") === "connected") {
      toast.success("Google Calendar collegato con successo");
      loadGcalStatus();
      // pulizia query string
      const url = new URL(window.location.href);
      url.searchParams.delete("google_calendar");
      window.history.replaceState({}, "", url.toString());
    }
  }, [loadGcalStatus]);
  const connectGcal = async () => {
    try {
      const { data } = await api.get("/oauth/calendar/authorize");
      if (data?.authorization_url) window.location.href = data.authorization_url;
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const disconnectGcal = async () => {
    if (!window.confirm("Scollegare Google Calendar dal tuo account?")) return;
    try { await api.post("/oauth/calendar/disconnect"); toast.success("Google Calendar scollegato"); loadGcalStatus(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const startSave = async () => {
    // 🆕 Feb 2026 — Un solo OTP: il SensitiveOtpGate ha già sbloccato la sessione, non serve
    // richiedere un secondo codice. Il PUT viene chiamato direttamente e il token è inviato
    // via header X-Sensitive-Otp-Token da api.js.
    setSaving(true);
    try {
      await api.put("/agency-settings", form);
      toast.success("Anagrafica agenzia aggiornata");
      refreshAgency();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // Retrocompat: mantenute per eventuali flussi con OTP dedicato (attualmente non usate dalla UI)
  const requestOtp = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/agency-settings/request-edit-otp");
      setOtpStage("otp");
      setOtpEmail(data.email || "");
      if (data.ok) toast.success(`Codice OTP inviato a ${data.email}`);
      else toast.error("Invio OTP fallito (verifica SMTP)");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const confirmSave = async () => {
    const code = (otpCode || "").trim();
    if (!/^\d{4,10}$/.test(code)) { toast.error("Inserisci il codice OTP"); return; }
    setSaving(true);
    try {
      await api.put("/agency-settings", form, { headers: { "X-Admin-OTP": code } });
      toast.success("Anagrafica agenzia aggiornata");
      refreshAgency();
      setOtpOpen(false);
      setOtpCode("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/agency-settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_url: data.logo_url }));
      toast.success("Logo caricato");
      refreshAgency();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeLogo = () => {
    setForm({ ...form, logo_url: "" });
    toast.info("Logo rimosso. Salva per confermare.");
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="agency-settings-page">
      <header className="flex items-center justify-between flex-wrap gap-4 print:hidden">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 mb-2">Amministrazione</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Anagrafica Agenzia</h1>
          <p className="text-slate-500 mt-1">Dati intestazione visibili in stampe, area cliente e mail ai partner</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="print-agency" onClick={() => window.print()}><Printer className="h-4 w-4 mr-2" /> Stampa</Button>
          <Button data-testid="save-agency-btn" disabled={saving} onClick={startSave} className="bg-[#0F172A] hover:bg-[#1E293B]">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Salvo…" : "Salva"}
          </Button>
        </div>
      </header>

      {/* 🆕 Feb 2026 — Scarica App Android / Crea collegamento Home (nascosto su desktop) */}
      <div className="rounded-lg border border-emerald-200 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 p-4 flex flex-wrap items-center justify-between gap-3 print:hidden" data-testid="settings-mobile-app-section">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-emerald-900 dark:text-emerald-100 flex items-center gap-2">
            📱 App mobile GestAgePro
          </div>
          <div className="text-xs text-emerald-800/80 dark:text-emerald-200/70 mt-0.5">
            Da smartphone Android puoi scaricare l'app; da iPhone/iPad crei un collegamento sulla Home. Da desktop il pulsante è nascosto.
          </div>
        </div>
        <MobileAppDownloadButton variant="inline" testIdPrefix="settings-mobile-app" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl">
        <div className="col-span-2 flex items-center gap-3 pb-3 border-b border-slate-100">
          <div className="h-10 w-10 rounded-md bg-blue-600 flex items-center justify-center text-white">
            <Building2 className="h-5 w-5" />
          </div>
          <span className="font-display text-xl font-semibold">Identità</span>
        </div>
        <div className="col-span-2"><Label>Nome commerciale</Label><Input data-testid="agency-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
        <div className="col-span-2"><Label>Ragione sociale</Label><Input data-testid="agency-legal-name" value={form.legal_name || ""} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} /></div>
        <div><Label>Partita IVA</Label><Input data-testid="agency-vat" value={form.vat_number || ""} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} /></div>
        <div><Label>Codice fiscale</Label><Input data-testid="agency-fiscal-code" value={form.fiscal_code || ""} onChange={(e) => setForm({ ...form, fiscal_code: e.target.value.toUpperCase() })} placeholder="RSSMRA80A01H501Z" /></div>
        <div><Label>Numero iscrizione RUI</Label><Input data-testid="agency-rui" value={form.rui_number || ""} onChange={(e) => setForm({ ...form, rui_number: e.target.value })} /></div>
        <div><Label>Data iscrizione RUI</Label><DatePicker data-testid="agency-rui-date" value={form.rui_date || ""} onChange={(e) => setForm({ ...form, rui_date: e.target.value })} /></div>
        <div><Label>Sito web</Label><Input data-testid="agency-website" value={form.website || ""} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="www.miaagenzia.it" /></div>
        <div className="col-span-2"><Label>Motto / Tagline</Label><Input data-testid="agency-motto" value={form.motto || ""} onChange={(e) => setForm({ ...form, motto: e.target.value })} placeholder='Es. "Proteggiamo ciò che ami"' /></div>
        <div className="col-span-2">
          <Label>Logo agenzia</Label>
          <div className="flex items-center gap-4 mt-2">
            <div className="h-20 w-20 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
              {form.logo_url ? (
                <img data-testid="agency-logo-preview" src={form.logo_url} alt="logo" className="h-full w-full object-cover" />
              ) : (
                <Building2 className="h-8 w-8 text-slate-300" />
              )}
            </div>
            <div className="space-y-2">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={uploadLogo} className="hidden" data-testid="agency-logo-file" />
              <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()}>
                <Upload className="h-3.5 w-3.5 mr-2" /> {uploading ? "Caricamento…" : (form.logo_url ? "Cambia logo" : "Carica logo")}
              </Button>
              {form.logo_url && (
                <Button type="button" variant="ghost" size="sm" onClick={removeLogo} className="text-red-600 ml-2">
                  <X className="h-3.5 w-3.5 mr-1" /> Rimuovi
                </Button>
              )}
              <p className="text-[11px] text-slate-500">PNG, JPG, WEBP o SVG · max 2 MB. Visibile in sidebar CRM, area cliente, area broker e stampe.</p>
            </div>
          </div>
        </div>

        <div className="col-span-2 mt-4 pb-3 border-b border-slate-100">
          <span className="font-display text-xl font-semibold">Contatti</span>
        </div>
        <div><Label>Telefono</Label><Input data-testid="agency-phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
        <div><Label>Email</Label><Input data-testid="agency-email" type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
        <div className="col-span-2"><Label>PEC</Label><Input data-testid="agency-pec" type="email" value={form.pec || ""} onChange={(e) => setForm({ ...form, pec: e.target.value })} /></div>

        <div className="col-span-2 mt-4 pb-3 border-b border-slate-100">
          <span className="font-display text-xl font-semibold">Sede</span>
        </div>
        <div className="col-span-2"><Label>Indirizzo</Label><Input data-testid="agency-address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
        <div><Label>Città</Label><Input data-testid="agency-city" value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
        <div><Label>CAP</Label><Input data-testid="agency-cap" value={form.cap || ""} onChange={(e) => setForm({ ...form, cap: e.target.value })} maxLength={5} /></div>
        <div><Label>Provincia</Label><Input data-testid="agency-province" value={form.province || ""} onChange={(e) => setForm({ ...form, province: e.target.value.toUpperCase().slice(0, 2) })} maxLength={2} /></div>

        {/* 🆕 Regime fiscale */}
        <div className="col-span-2 mt-4 pb-3 border-b border-slate-100 flex items-center gap-2">
          <span className="font-display text-xl font-semibold">💼 Regime fiscale</span>
          <span className="text-xs text-slate-500">Utilizzato in ricevute, fatture e documenti fiscali</span>
        </div>
        <div className="col-span-2">
          <Label>Regime attivo</Label>
          <select
            data-testid="agency-fiscal-regime"
            className="w-full h-9 mt-1 px-2 border border-slate-300 rounded bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            value={form.fiscal_regime || ""}
            onChange={(e) => setForm({ ...form, fiscal_regime: e.target.value })}
          >
            <option value="">— non specificato —</option>
            {FISCAL_REGIMES.map((r) => (
              <option key={r.code} value={r.code}>{r.label}</option>
            ))}
          </select>
          <p className="text-[10px] text-slate-500 mt-1">
            {form.fiscal_regime === "esente_iva_art10" && "✔ Ricevute con dicitura di esenzione IVA per intermediazione assicurativa."}
            {form.fiscal_regime === "forfettario" && "✔ Nessuna IVA applicata, nessuna ritenuta d'acconto per il committente."}
            {form.fiscal_regime === "ordinario" && "✔ IVA applicata secondo aliquota (di default 22%)."}
            {form.fiscal_regime === "semplificato" && "✔ Contabilità semplificata (art. 18 D.P.R. 600/73)."}
            {form.fiscal_regime === "minimi" && "⚠ Regime dei minimi (D.L. 98/2011) — sostituito dal forfettario dal 2015."}
            {form.fiscal_regime === "altro" && "Specifica il regime nel campo note qui sotto."}
          </p>
        </div>
        <div className="col-span-2">
          <Label>Nota / dicitura fiscale personalizzata</Label>
          <Input
            data-testid="agency-fiscal-regime-note"
            value={form.fiscal_regime_note || ""}
            onChange={(e) => setForm({ ...form, fiscal_regime_note: e.target.value })}
            placeholder={"Es. \"Operazione non soggetta a IVA ai sensi dell'art. 10, D.P.R. 633/72\""}
          />
          <p className="text-[10px] text-slate-500 mt-1">Facoltativa. Se compilata, verrà usata come dicitura di default nelle nuove ricevute.</p>
        </div>

        {/* 🆕 Feb 2026 — Personalizzazione timbro audit firma OTP */}
        <div className="col-span-2 mt-4 pb-3 border-b border-slate-100 flex items-center gap-2">
          <span className="font-display text-xl font-semibold">✍️ Timbro audit firma</span>
          <span className="text-xs text-slate-500">Colore dell&apos;header del PDF firmato — il logo agenzia viene usato automaticamente</span>
        </div>
        <div className="col-span-2">
          <Label>Colore header timbro</Label>
          <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="audit-stamp-color-presets">
            {[
              { c: "#2563EB", n: "Indigo" },
              { c: "#0EA5E9", n: "Sky" },
              { c: "#10B981", n: "Emerald" },
              { c: "#059669", n: "Green" },
              { c: "#F59E0B", n: "Amber" },
              { c: "#EF4444", n: "Rosso" },
              { c: "#EC4899", n: "Rosa" },
              { c: "#7C3AED", n: "Viola" },
              { c: "#0F172A", n: "Nero" },
            ].map((p) => {
              const isActive = (form.audit_stamp_color || "").toUpperCase() === p.c.toUpperCase();
              return (
                <button
                  key={p.c}
                  type="button"
                  onClick={() => setForm({ ...form, audit_stamp_color: p.c })}
                  data-testid={`audit-color-${p.c.slice(1)}`}
                  title={p.n}
                  className={`h-9 w-9 rounded-md border-2 transition-all ${isActive ? "border-slate-900 scale-110 shadow-md" : "border-slate-200 hover:scale-105"}`}
                  style={{ backgroundColor: p.c }}
                  aria-label={p.n}
                  aria-pressed={isActive}
                />
              );
            })}
            <input
              type="color"
              value={form.audit_stamp_color || "#2563EB"}
              onChange={(e) => setForm({ ...form, audit_stamp_color: e.target.value.toUpperCase() })}
              className="h-9 w-9 rounded-md border-2 border-dashed border-slate-300 cursor-pointer p-0.5"
              data-testid="audit-color-custom"
              title="Scegli un colore personalizzato"
            />
            <span className="text-xs font-mono text-slate-500 tabular-nums">{form.audit_stamp_color || "#2563EB (default)"}</span>
            {form.audit_stamp_color && (
              <button type="button" onClick={() => setForm({ ...form, audit_stamp_color: "" })} className="text-xs text-slate-500 hover:text-rose-600 underline" data-testid="audit-color-reset">
                Ripristina default
              </button>
            )}
          </div>
          <p className="text-[10px] text-slate-500 mt-2">
            Il colore viene applicato all&apos;header della pagina di audit del PDF firmato. Se hai caricato un logo agenzia, verrà mostrato nell&apos;angolo superiore destro del timbro.
          </p>
          {/* Preview */}
          <div className="mt-3 rounded-md overflow-hidden border border-slate-200 max-w-md">
            <div className="p-3 text-white" style={{ backgroundColor: form.audit_stamp_color || "#2563EB" }}>
              <div className="text-sm font-bold tracking-wide">TIMBRO AUDIT FIRMA OTP</div>
              <div className="text-[10px] opacity-90">Firma elettronica avanzata via One-Time Password</div>
            </div>
            <div className="p-2 text-[10px] text-slate-500 bg-white">Anteprima del blocco di intestazione</div>
          </div>
        </div>
      </div>

      {/* Sezione IMAP: spostata dentro "Impostazioni Email" (scheda "IMAP / Ricezione") per unificare la configurazione delle caselle di posta */}

      {/* 🆕 Test OTP — verifica dopo ogni deploy che l'SMTP invii realmente l'OTP al master_email dell'utente */}
      <TestOtpCard />

      {/* 🆕 Log Email — audit invii con esito e mittente SMTP usato */}
      <EmailAuditLogCard />

      {/* Dialog OTP per protezione modifiche anagrafica */}
      <Dialog open={otpOpen} onOpenChange={(o) => { if (!o && !saving) { setOtpOpen(false); setOtpCode(""); } }}>
        <DialogContent data-testid="agency-otp-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <ShieldCheck className="h-5 w-5" /> Conferma con OTP via email
            </DialogTitle>
            <DialogDescription>
              Per sicurezza la modifica dell&apos;anagrafica agenzia richiede un codice OTP.
            </DialogDescription>
          </DialogHeader>
          {otpStage === "confirm" && (
            <div className="text-sm text-slate-700 space-y-3">
              <p>Riceverai un codice a 6 cifre via email all&apos;indirizzo master configurato per l&apos;account admin.</p>
              <ul className="bg-slate-50 border border-slate-200 rounded px-3 py-2 text-xs text-slate-600 list-disc pl-6">
                <li>Denominazione: <strong>{form.name || "—"}</strong></li>
                <li>P.IVA: <strong>{form.vat_number || "—"}</strong></li>
                <li>PEC: <strong>{form.pec || "—"}</strong></li>
              </ul>
            </div>
          )}
          {otpStage === "otp" && (
            <div className="text-sm text-slate-700 space-y-3">
              <p>Codice OTP inviato a <strong>{otpEmail}</strong>. Inseriscilo per applicare le modifiche.</p>
              <Input
                data-testid="agency-otp-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="6 cifre"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="text-center text-lg font-mono tracking-[0.5em]"
              />
              <button type="button" onClick={requestOtp} disabled={saving} className="text-xs text-blue-600 hover:underline" data-testid="agency-otp-resend">
                Rigenera codice
              </button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOtpOpen(false); setOtpCode(""); }} disabled={saving}>Annulla</Button>
            {otpStage === "confirm" ? (
              <Button data-testid="agency-request-otp" onClick={requestOtp} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white">
                <Mail className="h-4 w-4 mr-1.5" /> {saving ? "Invio…" : "Invia codice OTP"}
              </Button>
            ) : (
              <Button data-testid="agency-confirm-save" onClick={confirmSave} disabled={saving || otpCode.length < 4} className="bg-[#0F172A] hover:bg-[#1E293B]">
                {saving ? "Salvataggio…" : "Salva con OTP"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ExpiryReminderDaysCard />

      {/* 🆕 Sezione: Google Calendar (OAuth) */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 mt-6 p-5" data-testid="gcal-section">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              🗓️ Google Calendar
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Collega il tuo account Google per sincronizzare appuntamenti e scadenze sul tuo calendario personale.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!gcalStatus.configured ? (
              <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">
                Non configurato lato server
              </span>
            ) : gcalStatus.connected ? (
              <>
                <span className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1" data-testid="gcal-connected-badge">
                  ✓ Collegato{gcalStatus.email ? ` — ${gcalStatus.email}` : ""}
                </span>
                <Button variant="outline" size="sm" onClick={disconnectGcal} data-testid="gcal-disconnect-btn">
                  Scollega
                </Button>
              </>
            ) : (
              <Button onClick={connectGcal} data-testid="gcal-connect-btn" className="bg-blue-600 hover:bg-blue-700">
                Connetti Google Calendar
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* 🆕 Sezione: Voci accessorie predefinite (gestione polizze/offerte) */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 mt-6 p-5" data-testid="accessory-items-section">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              💼 Voci accessorie predefinite
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Diritti d&apos;agenzia, spese di gestione, sconti standard ecc. Queste voci appariranno come scelta rapida quando aggiungi una &quot;Voce accessoria&quot; in un&apos;offerta o variante.
            </p>
          </div>
        </div>
        {/* Form crea/modifica */}
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 mb-3 p-3 bg-slate-50 rounded-md border border-slate-200">
          <div className="sm:col-span-4">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Nome voce</Label>
              <a
                href={`/admin/offer-line-items?return_to=${encodeURIComponent(window.location.pathname)}`}
                className="text-[10px] text-blue-600 hover:text-blue-800 hover:underline"
                title="Gestisci le Voci offerta dal menù"
              >
                Gestisci voci
              </a>
            </div>
            {(() => {
              // Match voce corrente per nome esatto sulla lista Voci offerta
              const selectedItem = offerItems.find((oi) => oi.name === accForm.name);
              return (
                <>
                  <Select
                    value={selectedItem ? selectedItem.id : (accForm.name ? "__custom__" : "")}
                    onValueChange={(v) => {
                      if (v === "__custom__") {
                        setAccForm({ ...accForm, name: accForm.name || "" });
                      } else {
                        const it = offerItems.find((oi) => oi.id === v);
                        if (it) {
                          setAccForm({
                            ...accForm,
                            name: it.name,
                            // precompila importo/descrizione se ancora vuoti
                            default_premium: accForm.default_premium === "" && it.default_premium != null ? String(it.default_premium) : accForm.default_premium,
                            default_description: accForm.default_description === "" && it.description ? it.description : accForm.default_description,
                          });
                        }
                      }
                    }}
                  >
                    <SelectTrigger className="h-8 text-sm" data-testid="acc-form-name-select">
                      <SelectValue placeholder="Seleziona voce dalla lista…" />
                    </SelectTrigger>
                    <SelectContent>
                      {offerItems.map((it) => (
                        <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>
                      ))}
                      <SelectItem value="__custom__">Altro / personalizzata…</SelectItem>
                    </SelectContent>
                  </Select>
                  {!selectedItem && (
                    <Input
                      data-testid="acc-form-name"
                      value={accForm.name}
                      onChange={(e) => setAccForm({ ...accForm, name: e.target.value })}
                      placeholder="Es. Diritti d'agenzia"
                      className="h-8 text-sm mt-1"
                    />
                  )}
                </>
              );
            })()}
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Importo €</Label>
            <Input
              data-testid="acc-form-premium"
              type="number"
              step="0.01"
              value={accForm.default_premium}
              onChange={(e) => setAccForm({ ...accForm, default_premium: e.target.value })}
              placeholder="30.00"
              className="h-8 text-sm"
            />
          </div>
          <div className="sm:col-span-4">
            <Label className="text-xs">Descrizione (opzionale)</Label>
            <Input
              data-testid="acc-form-description"
              value={accForm.default_description}
              onChange={(e) => setAccForm({ ...accForm, default_description: e.target.value })}
              placeholder="Mostrato nell'email al cliente"
              className="h-8 text-sm"
            />
          </div>
          <div className="sm:col-span-1">
            <Label className="text-xs">Ordine</Label>
            <Input
              data-testid="acc-form-order"
              type="number"
              value={accForm.sort_order}
              onChange={(e) => setAccForm({ ...accForm, sort_order: e.target.value })}
              className="h-8 text-sm"
            />
          </div>
          <div className="sm:col-span-1 flex items-end gap-1">
            <Button
              type="button"
              size="sm"
              className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white"
              onClick={submitAccItem}
              data-testid="acc-form-submit"
            >
              {accEditingId ? "Salva" : "+ Aggiungi"}
            </Button>
            {accEditingId && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                onClick={() => { setAccEditingId(null); setAccForm({ name: "", default_premium: "", default_description: "", is_active: true, sort_order: 0 }); }}
              >
                X
              </Button>
            )}
          </div>
        </div>
        {/* Lista */}
        {accItems.length === 0 ? (
          <p className="text-xs text-slate-500 italic px-2 py-3">Nessuna voce accessoria configurata. Aggiungi la prima usando il form sopra.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="acc-items-table">
              <thead>
                <tr className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                  <th className="text-left px-3 py-2">Ordine</th>
                  <th className="text-left px-3 py-2">Nome</th>
                  <th className="text-right px-3 py-2">Importo</th>
                  <th className="text-left px-3 py-2">Descrizione</th>
                  <th className="text-center px-3 py-2">Attiva</th>
                  <th className="text-right px-3 py-2">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {accItems.map((it) => (
                  <tr key={it.id} className="border-t border-slate-100" data-testid={`acc-item-${it.id}`}>
                    <td className="px-3 py-2 text-slate-500 tabular-nums">{it.sort_order || 0}</td>
                    <td className="px-3 py-2 font-medium">{it.name}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {it.default_premium != null ? formatCurrency(it.default_premium) : "—"}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">{it.description || it.default_description || "—"}</td>
                    <td className="px-3 py-2 text-center">
                      {it.is_active !== false ? <span className="text-emerald-600">✓</span> : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => editAccItem(it)}
                        className="text-xs px-2 py-1 rounded border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 mr-1"
                        data-testid={`acc-item-edit-${it.id}`}
                      >
                        Modifica
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteAccItem(it)}
                        className="text-xs px-2 py-1 rounded border border-rose-300 bg-rose-50 hover:bg-rose-100 text-rose-700"
                        data-testid={`acc-item-delete-${it.id}`}
                      >
                        Elimina
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>


    </div>
  );
}


function TestOtpCard() {
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null); // { ok, sent_to_masked, when }
  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/me/test-otp");
      setLastResult({ ok: true, sent_to_masked: data.sent_to_masked, sent_to: data.sent_to, when: new Date() });
      toast.success(`OTP di test inviato a ${data.sent_to_masked}`);
    } catch (e) {
      setLastResult({ ok: false, error: formatApiError(e), when: new Date() });
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 max-w-3xl" data-testid="test-otp-card">
      <div className="flex items-center gap-3 pb-3 border-b border-slate-100 mb-4">
        <div className="h-10 w-10 rounded-md bg-emerald-600 flex items-center justify-center text-white">
          <Mail className="h-5 w-5" />
        </div>
        <div>
          <span className="font-display text-xl font-semibold block">Test OTP</span>
          <span className="text-xs text-slate-500">Verifica che l&apos;SMTP e la <em>master_email</em> siano configurati correttamente</span>
        </div>
      </div>
      <p className="text-sm text-slate-600 mb-4">
        Invia un OTP <strong>fittizio</strong> (non valido per il login) all&apos;indirizzo <em>master_email</em> del tuo profilo.
        Usalo per verificare, dopo ogni deploy o modifica SMTP, che i codici di sicurezza arrivino davvero.
      </p>
      <div className="flex items-center gap-3 flex-wrap">
        <Button
          data-testid="send-test-otp-btn"
          onClick={send}
          disabled={sending}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          <Mail className="h-4 w-4 mr-2" /> {sending ? "Invio in corso…" : "Invia OTP di test"}
        </Button>
        {lastResult && lastResult.ok && (
          <span data-testid="test-otp-success" className="text-sm text-emerald-700">
            ✅ Inviato a <strong>{lastResult.sent_to_masked}</strong> alle {lastResult.when.toLocaleTimeString("it-IT")}
          </span>
        )}
        {lastResult && !lastResult.ok && (
          <span data-testid="test-otp-error" className="text-sm text-red-600">
            ❌ {lastResult.error}
          </span>
        )}
      </div>
      <p className="text-[11px] text-slate-400 mt-3">
        Il codice mostrato nell&apos;email è solo dimostrativo — <strong>non può</strong> essere usato per accedere. Rate limit: 1 invio ogni 30 secondi.
      </p>
    </div>
  );
}



function EmailAuditLogCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(""); // "", "ok", "failed"
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [total, setTotal] = useState(0);
  // 🆕 Feb 2026 — Sezione comprimibile (persistita in localStorage)
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("email_audit_log_collapsed") === "1"; } catch { return false; }
  });
  const toggleCollapsed = () => {
    setCollapsed((v) => {
      const nv = !v;
      try { localStorage.setItem("email_audit_log_collapsed", nv ? "1" : "0"); } catch { /* noop */ }
      return nv;
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (search.trim()) params.set("q", search.trim());
      params.set("limit", "100");
      const { data } = await api.get(`/email-audit-log?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [statusFilter, search]);
  useEffect(() => { load(); }, [load]);

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString("it-IT"); }
    catch { return iso; }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 max-w-4xl" data-testid="email-audit-log-card">
      <button
        type="button"
        onClick={toggleCollapsed}
        className="w-full flex items-center gap-3 pb-3 border-b border-slate-100 mb-4 text-left hover:bg-slate-50/60 -mx-2 px-2 py-1 rounded transition-colors"
        data-testid="email-audit-log-toggle"
        aria-expanded={!collapsed}
      >
        <div className="h-10 w-10 rounded-md bg-indigo-600 flex items-center justify-center text-white">
          <Mail className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <span className="font-display text-xl font-semibold block">Log Email — Audit invii</span>
          <span className="text-xs text-slate-500">
            {collapsed
              ? <>Sezione compressa · {total} email totali registrate · Clicca per espandere</>
              : <>Ogni email inviata dal CRM (OTP, promemoria, notifiche, offerte) viene registrata qui con esito e mittente SMTP. Retention 90 giorni.</>
            }
          </span>
        </div>
        {collapsed ? <ChevronDown className="h-5 w-5 text-slate-500 shrink-0" /> : <ChevronUp className="h-5 w-5 text-slate-500 shrink-0" />}
      </button>
      {!collapsed && (<>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <select
          data-testid="email-log-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 px-2 border border-slate-300 rounded bg-white text-sm"
        >
          <option value="">Tutti gli stati</option>
          <option value="ok">✅ Solo inviate con successo</option>
          <option value="failed">❌ Solo fallite</option>
        </select>
        <input
          data-testid="email-log-search"
          type="text"
          placeholder="Cerca per oggetto, destinatario o mittente…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-9 px-3 border border-slate-300 rounded bg-white text-sm flex-1 min-w-[200px]"
        />
        <Button
          data-testid="email-log-refresh"
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
        >
          {loading ? "…" : "🔄 Aggiorna"}
        </Button>
        <span className="text-xs text-slate-500 ml-auto">
          {items.length} mostrati · {total} totali
        </span>
      </div>
      {loading ? (
        <div className="py-6 text-center text-sm text-slate-400">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="py-6 text-center text-sm text-slate-400">
          Nessun invio registrato con questi filtri.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase tracking-wide">
              <tr className="border-b border-slate-100">
                <th className="text-left px-2 py-2 font-semibold">Stato</th>
                <th className="text-left px-2 py-2 font-semibold">Data</th>
                <th className="text-left px-2 py-2 font-semibold">Destinatari</th>
                <th className="text-left px-2 py-2 font-semibold">Oggetto</th>
                <th className="text-left px-2 py-2 font-semibold">Mittente SMTP</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const isOk = it.status === "ok";
                const isExpanded = expandedId === it.id;
                return (
                  <React.Fragment key={it.id}>
                    <tr
                      className={`border-b border-slate-50 cursor-pointer hover:bg-slate-50 ${isOk ? "" : "bg-red-50/50"}`}
                      onClick={() => setExpandedId(isExpanded ? null : it.id)}
                      data-testid={`email-log-row-${it.id}`}
                    >
                      <td className="px-2 py-2">
                        {isOk ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700">✅ OK</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">❌ Fallita</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-xs text-slate-600 whitespace-nowrap">{fmtDate(it.sent_at)}</td>
                      <td className="px-2 py-2 text-xs">
                        {(it.to || []).slice(0, 2).join(", ")}
                        {(it.to || []).length > 2 && <span className="text-slate-400"> +{it.to.length - 2}</span>}
                      </td>
                      <td className="px-2 py-2 text-xs truncate max-w-[280px]" title={it.subject}>{it.subject || "(senza oggetto)"}</td>
                      <td className="px-2 py-2 text-xs text-slate-500 truncate max-w-[200px]" title={it.from_email}>{it.from_email || "—"}</td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-slate-50">
                        <td colSpan={5} className="px-4 py-3 text-xs text-slate-700 space-y-1">
                          <div><strong>ID log:</strong> <code className="text-[10px]">{it.id}</code></div>
                          <div><strong>Message-ID:</strong> <code className="text-[10px]">{it.message_id || "—"}</code></div>
                          <div><strong>SMTP host:</strong> {it.smtp_host}:{it.smtp_port}</div>
                          <div><strong>Utente che ha triggerato:</strong> {it.user_email || "—"} <span className="text-slate-400">({it.user_id || "n/a"})</span></div>
                          {(it.cc || []).length > 0 && <div><strong>CC:</strong> {it.cc.join(", ")}</div>}
                          {(it.bcc || []).length > 0 && <div><strong>BCC:</strong> {it.bcc.join(", ")}</div>}
                          {it.has_attachments && <div><strong>Allegati:</strong> {it.attachments_count}</div>}
                          {!isOk && it.error && (
                            <div className="mt-2 p-2 bg-red-100 border border-red-200 rounded">
                              <strong className="text-red-800">Errore SMTP:</strong>
                              <div className="font-mono text-[10px] text-red-900 mt-1 break-all whitespace-pre-wrap">{it.error}</div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      </>)}
    </div>
  );
}

